/**
 * Вход слоя движков: выбор движка (`?engine=`, `?renderer=`), его загрузка, прогрев в лобби, парковка, смена на лету и
 * хук для React. Здесь же множитель времени сервера. Код движков в главный бандл не попадает: каждый — свой
 * динамический `import()` (на нём Vite режет чанки), а из `base.ts` берутся только типы. Блоков движок не знает:
 * что строить на нём, приложению (`app.scene.ts`) решать.
 */

import { useEffect, useState, useSyncExternalStore, type RefObject } from 'react'
import type Phaser from 'phaser'
import type { Engine, EngineKind } from './base.js'

// === время ===================================================================

/**
 * Во сколько раз показ хода идёт быстрее: множитель задаёт сервер (`settings.speed`, в игре единица), клиент
 * лишь следует ему. Сквозные проверки не ждут анимаций. Меняются только длительности плана и движений интерфейса
 * (раскрытие и уход окон — `scaled` и `--time-scale` в стилях; показы сцены — `Engine.speed`); формы траекторий, их порядок и
 * события те же.
 */
let timeScale = 1
export const setTimeScale = (k: number): void => {
  timeScale = Math.max(1, k || 1)
  if (held?.engine) held.engine.speed = timeScale
  if (typeof document !== 'undefined')
    document.documentElement.style.setProperty('--time-scale', String(timeScale))
}
/** Длительность движения интерфейса с учётом множителя сервера. */
export const scaled = (ms: number): number => ms / timeScale

// === выбор движка ==========================================================
/*
 * Выбор реализации сцены: `?engine=custom|pixi|phaser|two|null` и `?renderer=` в адресе (`dom|svg|canvas` у `html`,
 * `webgl|webgpu|canvas` у `pixi`, `webgl|canvas` у `phaser`, `svg|canvas|webgl` у `two`); без них и при незнакомых
 * именах — `html:svg`. Меняется и на лету: `chooseEngine` (`host.ts`) перезаписывает адрес без перезагрузки, а
 * `useEngine` пересоздаёт реализацию. Каждая реализация — свой динамический импорт, то есть свой JS-бандл: пока она не
 * выбрана, её код (а у `pixi` и `phaser` и код выбранного отрисовщика) не загружается.
 */

const ENGINE_KINDS: readonly EngineKind[] = ['html', 'pixi', 'phaser', 'two', 'null']

/** Отрисовщик холста: у PixiJS 8 — по имени из его `preference`, у Phaser 4 — `webgl` или `canvas`. */
export type CanvasRenderer = 'webgl' | 'webgpu' | 'canvas'

/** Отрисовщик реализации: холст (`CanvasRenderer`) или разметка (`dom`, `svg`). */
export type SceneRenderer = CanvasRenderer | 'dom' | 'svg'

/** Какие отрисовщики есть у реализаций и какой из них первый (по нему выбирается просьба без названия). */
const RENDERERS: Partial<Record<EngineKind, readonly SceneRenderer[]>> = {
  html: ['svg', 'dom', 'canvas'],
  pixi: ['webgl', 'webgpu', 'canvas'],
  phaser: ['webgl', 'canvas'],
  two: ['webgl', 'svg', 'canvas'],
}

interface EngineChoice {
  kind: EngineKind
  /** У `html`, `pixi`, `phaser` и `two`. */
  renderer: SceneRenderer
}

/** Реализация по умолчанию: ей возвращается страница, когда выбранная отказала. */
export const DEFAULT_ENGINE = 'html:svg'

/** Что показывает переключатель: значение — `kind` или `kind:renderer`. */
export const ENGINE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'html:svg', label: 'html · SVG' },
  { value: 'html:dom', label: 'html · DOM' },
  { value: 'html:canvas', label: 'html · Canvas' },
  { value: 'pixi:webgl', label: 'pixi · WebGL' },
  { value: 'pixi:webgpu', label: 'pixi · WebGPU' },
  { value: 'pixi:canvas', label: 'pixi · Canvas' },
  { value: 'phaser:webgl', label: 'phaser · WebGL' },
  { value: 'phaser:canvas', label: 'phaser · Canvas' },
  { value: 'two:svg', label: 'two · SVG' },
  { value: 'two:canvas', label: 'two · Canvas' },
  { value: 'two:webgl', label: 'two · WebGL' },
]

const isKind = (value: string | null | undefined): value is EngineKind =>
  !!value && (ENGINE_KINDS as readonly string[]).includes(value)

/** Выбор из `kind` и `renderer`; незнакомое — по умолчанию. */
function make(kind: string | null | undefined, renderer: string | null | undefined): EngineChoice {
  if (!isKind(kind)) return parse(DEFAULT_ENGINE)
  const list = RENDERERS[kind]
  return { kind, renderer: list?.find((r) => r === renderer) ?? list?.[0] ?? 'webgl' }
}

function parse(value: string): EngineChoice {
  const [kind, renderer] = value.split(':')
  return make(kind, renderer)
}

function read(): EngineChoice {
  const query = typeof location === 'undefined' ? new URLSearchParams() : new URLSearchParams(location.search)
  return make(query.get('engine'), query.get('renderer'))
}

let choice = read()
const listeners = new Set<() => void>()

/** Текущий выбор. */
const currentChoice = (): EngineChoice => choice

/** Значение переключателя для выбора. */
export const choiceValue = (c: EngineChoice): string =>
  RENDERERS[c.kind] ? `${c.kind}:${c.renderer}` : c.kind

/** Выбрать реализацию: `html:svg`, `pixi:webgpu` и так далее. Адрес обновляется, страница не перезагружается. */
export function choose(value: string): EngineChoice {
  choice = parse(value)
  if (typeof history !== 'undefined') {
    const url = new URL(location.href)
    if (choiceValue(choice) === DEFAULT_ENGINE) {
      url.searchParams.delete('engine')
      url.searchParams.delete('renderer')
    } else {
      url.searchParams.set('engine', choice.kind)
      if (RENDERERS[choice.kind]) url.searchParams.set('renderer', choice.renderer)
      else url.searchParams.delete('renderer')
    }
    history.replaceState(history.state, '', url)
  }
  return choice
}

/** Выбор сменился: подписчики (`useEngineChoice`) перечитывают его. */
function announceChoice(): void {
  for (const listener of listeners) listener()
}

export function useEngineChoice(): EngineChoice {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => choice,
  )
}

/*
 * Сообщение о том, что реализация сменилась без ведома игрока: отказала отрисовка и партия ушла на
 * реализацию по умолчанию, либо Pixi подменил просимый отрисовщик. Показывает его плашка `EngineNote` (как «Connection lost»).
 */
interface EngineNotice {
  id: number
  title: string
  detail: string
}

let notice: EngineNotice | null = null
const noticeListeners = new Set<() => void>()

export function noticeEngine(title: string, detail: string): void {
  notice = { id: (notice?.id ?? 0) + 1, title, detail }
  for (const listener of noticeListeners) listener()
}

export function dismissEngineNotice(): void {
  notice = null
  for (const listener of noticeListeners) listener()
}

export function useEngineNotice(): EngineNotice | null {
  return useSyncExternalStore(
    (listener) => {
      noticeListeners.add(listener)
      return () => noticeListeners.delete(listener)
    },
    () => notice,
  )
}

/** Название выбора для людей: «pixi · WebGL». */
const engineLabel = (c: EngineChoice): string =>
  ENGINE_OPTIONS.find((o) => o.value === choiceValue(c))?.label ?? c.kind

/**
 * Загрузка кода реализации и всего, что не трогает звук, — до первого жеста: сама реализация создаёт звуковой контекст,
 * а браузеры разрешают это только по жесту. Результат — фабрика, ею реализация создаётся уже после жеста.
 */
const LOADERS: Record<EngineKind, (c: EngineChoice) => Promise<() => Engine>> = {
  html: (c) => import('./html.js').then((m) => () => new m.HtmlEngine(c.renderer)),
  pixi: (c) => import('./pixi.js').then((m) => () => new m.PixiEngine(c.renderer as CanvasRenderer)),
  // Phaser собран по отрисовщикам: грузится только копия выбранного, с нужными модулями (`vite.config.ts`).
  phaser: async (c) => {
    // `virtual:phaser-*` — модули плагина `phaser-backends` (`vite.config.ts`): файлов у них нет, а значит, и типов.
    const [lib, m] = await Promise.all([
      c.renderer === 'canvas'
        ? // @ts-expect-error нет типов у виртуального модуля
          import('virtual:phaser-canvas')
        : // @ts-expect-error нет типов у виртуального модуля
          import('virtual:phaser-webgl'),
      import('./phaser.js'),
    ])
    return () => new m.PhaserEngine(lib.default as typeof Phaser, c.renderer as CanvasRenderer)
  },
  two: (c) => import('./two.js').then((m) => () => new m.TwoEngine(c.renderer)),
  null: () => import('./base.js').then((m) => () => new m.LogEngine()),
}

const loads = new Map<string, Promise<() => Engine>>()
function loadEngine(c: EngineChoice): Promise<() => Engine> {
  const value = choiceValue(c)
  let loading = loads.get(value)
  if (!loading) {
    loads.set(value, (loading = LOADERS[c.kind](c)))
    // Отказ не кэшируем: следующая попытка загрузит снова.
    loading.catch(() => loads.delete(value))
  }
  return loading
}

// === хозяйство ===============================================================

/** Что строится на движке: поле в сборе, для лобби и для партии. */
export interface Scene {
  /** Спрятать сцену (`true`) или показать (`false`). */
  park?(on: boolean): void
  /** Пустая сцена: она ждёт партию. */
  reset?(): void
  destroy(): void
}

/**
 * Что движок строит на себе. Код блоков и звуки грузятся лениво (лобби их не тянет), поэтому приложение отдаёт
 * не сцену, а загрузчик. Движок блоков не знает: их ему передаёт приложение (`app.scene.ts`).
 */
export type Scenery<S extends Scene> = () => Promise<(engine: Engine) => Promise<S | null>>

/** Выбрать движок: `html:svg`, `pixi:webgpu` и так далее. Адрес обновляется, страница не перезагружается. */
export function chooseEngine(value: string): void {
  const next = choose(value)
  // Прежний движок лобби не нужен: освобождаем его (контекст WebGL), и новый встаёт на его место прогретым. Тот, что держит партия, снимает сама партия.
  if (held && !held.taken && held.value !== choiceValue(next)) drop()
  if (gestured && !held) raise()
  announceChoice()
}

/*
 * Движок приложения. Он один на страницу и живёт не с партией, а с лобби: пока игрок в лобби, он уже собран
 * (холст, шейдеры, текстуры) и спрятан, а партия берёт готовый — вход в неё не платит за создание.
 * Его узел-хост лежит в парковке (окно размером с страницу, невидимое) и переезжает в хост партии целиком, вместе с
 * холстом: контекст WebGL от переноса не теряется. Создание и звук — одно место, `raise`, и зовут его и прогрев в
 * лобби, и партия.
 */
interface Held {
  /** Выбор, под который он собран (`choiceValue`). */
  readonly value: string
  readonly el: HTMLElement
  engine: Engine | null
  scene: Scene | null
  /** Собран и смонтирован; `null` — отказал (приложение уже ушло на движок по умолчанию). */
  ready: Promise<{ engine: Engine; scene: Scene } | null>
  /** Партия держит его у себя. */
  taken: boolean
  off: () => void
}

let held: Held | null = null
/** Что строить на движке: задаёт тот, кто первым попросил (`warmUp` или `useEngine`). */
let known: Scenery<Scene> | null = null
/** `warmUp` зван: движок остаётся прогретым и в лобби. */
let warm = false
/** Жест уже был: можно создавать звуковой контекст. */
let gestured = false

function parking(): HTMLElement {
  let el = document.getElementById('engine-parking')
  if (!el) {
    el = document.createElement('div')
    el.id = 'engine-parking'
    el.setAttribute('aria-hidden', 'true')
    el.style.cssText =
      'position:fixed;inset:0;visibility:hidden;pointer-events:none;overflow:hidden;z-index:-1'
    document.body.append(el)
  }
  return el
}

/** Движок текущего выбора: созданный раньше или новый, в `into` (по умолчанию — парковка). */
function raise(into?: HTMLElement): Held | null {
  const scenery = known
  if (!scenery) return null
  const c = { ...currentChoice() }
  const value = choiceValue(c)
  if (held?.value === value) return held
  drop()
  const el = document.createElement('div')
  el.className = 'absolute inset-0'
  ;(into ?? parking()).append(el)
  const h: Held = {
    value,
    el,
    engine: null,
    scene: null,
    ready: Promise.resolve(null),
    taken: !!into,
    off: () => {},
  }
  const fail = (error: unknown) => {
    console.warn(`[engine] ${c.kind}: ${error instanceof Error ? error.message : String(error)}`)
    if (held === h && choiceValue(c) !== DEFAULT_ENGINE) {
      noticeEngine('Scene engine switched', `${engineLabel(c)} failed. Using ${DEFAULT_ENGINE} instead.`)
      chooseEngine(DEFAULT_ENGINE)
    }
  }
  held = h
  h.ready = Promise.all([loadEngine(c), scenery()])
    .then(async ([make, build]) => {
      if (held !== h) return null
      const engine = (h.engine = make())
      engine.speed = timeScale
      h.off = engine.events.on('error', fail)
      await engine.mount(el)
      if (held !== h) return null
      const scene = await build(engine)
      if (held !== h || !scene) {
        scene?.destroy()
        return null
      }
      h.scene = scene
      // Пара кадров на шейдеры и текстуры, затем сон до партии.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (held === h && !h.taken) scene.park?.(true)
        }),
      )
      return { engine, scene }
    })
    .catch((error) => {
      fail(error)
      return null
    })
  return h
}

/** Снять движок: он больше не нужен или выбран другой. */
function drop(): void {
  const h = held
  held = null
  if (!h) return
  h.off()
  h.scene?.destroy()
  h.engine?.destroy()
  h.el.remove()
}

/**
 * Прогрев движка, пока игрок в лобби. Код движка грузится сразу, сам он собирается на первом жесте:
 * он создаёт звуковой контекст, а браузеры разрешают это только по жесту (иначе предупреждение, и контекст «спит»).
 * Игрок заходит в лобби и создаёт комнату раньше, чем начнётся партия.
 */
export function warmUp(scenery: Scenery<Scene>): void {
  if (typeof window === 'undefined' || warm) return
  warm = true
  known = scenery
  const events = ['pointerdown', 'keydown']
  const go = () => {
    for (const type of events) removeEventListener(type, go, true)
    gestured = true
    raise()
  }
  void loadEngine(currentChoice()).catch(() => {})
  if (navigator.userActivation?.hasBeenActive) go()
  else for (const type of events) addEventListener(type, go, { capture: true })
}

/** Уход из партии прячет движок в парковку, а не разбирает. */
function leave(h: Held): void {
  if (held !== h) return
  h.taken = false
  if (!warm || choiceValue(currentChoice()) !== h.value) return drop()
  h.scene?.reset?.()
  h.scene?.park?.(true)
  parking().append(h.el)
}

/** Сцена партии на движке текущего выбора; `null`, пока он собирается. */
export function useEngine<S extends Scene>(
  host: RefObject<HTMLElement | null>,
  scenery: Scenery<S>,
): S | null {
  const { kind, renderer } = useEngineChoice()
  const [scene, setScene] = useState<S | null>(null)

  useEffect(() => {
    const into = host.current
    if (!into) return
    known = scenery as Scenery<Scene>
    let live = true
    const h = raise(into)
    if (!h) return
    h.taken = true
    void h.ready.then((next) => {
      if (!live || !next) return
      into.append(h.el)
      next.scene.park?.(false)
      setScene(next.scene as S)
    })
    return () => {
      live = false
      setScene(null)
      leave(h)
    }
  }, [host, scenery, kind, renderer])

  return scene
}
